/**
 * A permanent strip across the top of every screen when this is NOT the real ProTrack.
 *
 * The email markers stop a test NOTIFICATION being mistaken for a real one. This stops the
 * reverse: somebody doing real work in the test copy, or reporting a "bug" that is really just
 * staging data. Both mistakes come from the two systems being indistinguishable at a glance.
 *
 * Derived from the hostname, not from a build variable. A build-time flag has to be set
 * correctly when the image is built; a hostname is simply true. Production has no "staging" in
 * its address and shows nothing at all.
 */
import React, { useLayoutEffect, useRef } from 'react'

const NON_PRODUCTION = ['staging', 'stage', 'test', 'dev', 'localhost', '127.0.0.1', 'qa', 'demo']

export function environmentName() {
  if (typeof window === 'undefined') return null
  const host = (window.location.hostname || '').toLowerCase()
  const hit = NON_PRODUCTION.find((t) => host === t || host.startsWith(`${t}.`) || host.includes(`.${t}.`) || host.includes(`${t}-`))
  return hit ? (hit === '127.0.0.1' ? 'localhost' : hit) : null
}

export default function EnvironmentBanner() {
  const env = environmentName()
  const ref = useRef(null)

  // The app below is one screen tall (h-screen), so a banner above it makes the page overflow and the
  // banner scrolls away. Hand its height to the stylesheet (index.css), which takes it off the app's
  // height: the strip stays put and nothing scrolls twice. Production has no banner and sets nothing.
  useLayoutEffect(() => {
    if (!env || !ref.current) return undefined
    const root = document.documentElement
    const set = () => root.style.setProperty('--env-banner-h', `${ref.current.offsetHeight}px`)
    root.classList.add('has-env-banner')
    set()
    window.addEventListener('resize', set)       // the text wraps to two lines on a narrow screen
    return () => { window.removeEventListener('resize', set); root.classList.remove('has-env-banner') }
  }, [env])

  if (!env) return null       // production: nothing, ever

  return (
    <div
      ref={ref}
      role="status"
      className="sticky top-0 z-[60] w-full bg-red-700 text-white text-center py-1.5 px-4 text-xs font-semibold tracking-wide"
      style={{ letterSpacing: '0.04em' }}
    >
      ⚠️ {env.toUpperCase()} — TEST COPY OF PROTRACK. Nothing here is real: no real consultant,
      no real deliverable, no real money.
    </div>
  )
}
